/**
 * celebration.service.ts
 *
 * Celebration system — trigger evaluation, storage, deduplication.
 *
 * Fixes applied (v2):
 *   - Local timezone used for midnight reset (not UTC)
 *   - Retroactive edit guard: weight triggers only fire on days with new entries
 *   - baselineWeight read from UserProfile (typed correctly)
 *   - first_week_complete Gold trigger added
 *   - CelebrationRecord gains version, deviceDate, source fields
 *   - All tiers stored in Firestore
 *
 * v3 — Canonical storage model:
 *   - Gold: one document per eventKey, written once, never updated
 *   - Bronze/Silver: one canonical document per eventKey, count incremented monthly
 *   - Doc ID: {userId}_{eventKey} for stable addressing
 *   - Monthly gate: Bronze/Silver fire at most once per calendar month
 *   - logging_habit_forming threshold raised to 6/7 days
 *   - migrateCelebrations() collapses legacy duplicate docs
 */

import {
  getCollection,
  setDocumentWithId,
  getDocument,
  deleteDocument,
} from './firebase/firestore.service';
import { where, orderBy } from 'firebase/firestore';
import { getDailyNutrition, getTargetRanges } from './nutrition.service';
import { getBodyMetrics, getDailyTDEECached } from './tdee.service';
import { getBodyStatsEntriesForDateRange } from './bodyStats.service';
import { getUserProfile } from './userProfile.service';
import { getWorkoutEntriesForDateRange } from './workout.service';
import { getTodayDate, getDateDaysAgo } from '../utils/date';
import type { CelebrationTier } from '../components/ui/Balloon';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CelebrationRecord {
  id?: string;
  userId: string;
  tier: CelebrationTier;
  eventKey: string;
  title: string;
  message: string;
  subtext?: string;
  // Canonical fields (v3)
  count: number;                  // times awarded (1 for Gold, N for Bronze/Silver)
  firstAwardedAt: number;         // timestamp of first award
  lastAwardedAt: number;          // timestamp of most recent award
  lastAwardedMonth?: string;      // YYYY-MM — Bronze/Silver monthly gate
  // Legacy fields (kept for backward compat)
  earnedAt: number;
  metricValue?: number;
  metricUnit?: string;
  shownAt?: number;
  sourceScreen?: string;
  version: number;
  deviceDate: string;
  source: 'weight' | 'nutrition' | 'hydration' | 'system' | 'workout';
}

export interface CelebrationResult {
  tier: CelebrationTier;
  eventKey: string;
  message: string;
  subtext?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CELEBRATIONS_COLLECTION = 'celebrations';
const RECORD_VERSION = 1;

const DAILY_GATE_KEY = 'coretrak.celebration.shownDate';

const WHTR_LOW  = 0.50;
const WHTR_HIGH = 0.60;

// ─── Local date helper ────────────────────────────────────────────────────────
// Uses device local time — never UTC — so midnight resets match the user's day.

function getLocalDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─── Daily gate ───────────────────────────────────────────────────────────────

export function hasShownCelebrationToday(): boolean {
  try {
    return localStorage.getItem(DAILY_GATE_KEY) === getLocalDateString();
  } catch {
    return false;
  }
}

export function markCelebrationShownToday(): void {
  try {
    localStorage.setItem(DAILY_GATE_KEY, getLocalDateString());
  } catch { /* best effort */ }
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

// Canonical doc ID: stable per user+eventKey
function canonicalDocId(userId: string, eventKey: string): string {
  return `${userId}_${eventKey}`;
}

// Current calendar month as YYYY-MM (local time)
function getCurrentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export async function getCelebrations(userId: string): Promise<CelebrationRecord[]> {
  return getCollection<CelebrationRecord>(
    CELEBRATIONS_COLLECTION,
    where('userId', '==', userId),
    orderBy('earnedAt', 'desc'),
  );
}

export async function getGoldAchievements(userId: string): Promise<CelebrationRecord[]> {
  return getCollection<CelebrationRecord>(
    CELEBRATIONS_COLLECTION,
    where('userId', '==', userId),
    where('tier', '==', 'gold'),
    orderBy('earnedAt', 'desc'),
  );
}

// Gold deduplication — check canonical doc exists
async function hasEventBeenAwarded(userId: string, eventKey: string): Promise<boolean> {
  const docId = canonicalDocId(userId, eventKey);
  const existing = await getDocument<CelebrationRecord>(`${CELEBRATIONS_COLLECTION}/${docId}`);
  return existing !== null;
}

// Bronze/Silver monthly gate — check canonical doc's lastAwardedMonth
async function hasEventBeenAwardedThisMonth(userId: string, eventKey: string): Promise<boolean> {
  const docId = canonicalDocId(userId, eventKey);
  const existing = await getDocument<CelebrationRecord>(`${CELEBRATIONS_COLLECTION}/${docId}`);
  if (!existing) return false;
  return existing.lastAwardedMonth === getCurrentMonth();
}

// Gold — write canonical doc once, never update
async function storeGoldCelebration(record: Omit<CelebrationRecord, 'id'>): Promise<void> {
  const docId = canonicalDocId(record.userId, record.eventKey);
  await setDocumentWithId(CELEBRATIONS_COLLECTION, docId, {
    ...record,
    count: 1,
    firstAwardedAt: record.earnedAt,
    lastAwardedAt: record.earnedAt,
  });
}

// Bronze/Silver — upsert canonical doc, incrementing count and updating month
async function upsertRepeatableCelebration(record: Omit<CelebrationRecord, 'id'>): Promise<void> {
  const docId = canonicalDocId(record.userId, record.eventKey);
  const existing = await getDocument<CelebrationRecord>(`${CELEBRATIONS_COLLECTION}/${docId}`);
  const now = record.earnedAt;
  const month = getCurrentMonth();
  if (existing) {
    await setDocumentWithId(CELEBRATIONS_COLLECTION, docId, {
      ...existing,
      title: record.title,
      message: record.message,
      count: (existing.count ?? 1) + 1,
      lastAwardedAt: now,
      lastAwardedMonth: month,
      earnedAt: now,
      shownAt: now,
      sourceScreen: record.sourceScreen,
      deviceDate: record.deviceDate,
    }, { merge: true });
  } else {
    await setDocumentWithId(CELEBRATIONS_COLLECTION, docId, {
      ...record,
      count: 1,
      firstAwardedAt: now,
      lastAwardedAt: now,
      lastAwardedMonth: month,
    });
  }
}

// ─── Migration ────────────────────────────────────────────────────────────────
// Collapses legacy duplicate docs into canonical one-per-eventKey model.
// Safe to call multiple times — idempotent.
export async function migrateCelebrations(userId: string): Promise<void> {
  const allRecords = await getCollection<CelebrationRecord>(
    CELEBRATIONS_COLLECTION,
    where('userId', '==', userId),
  );

  // Group by eventKey
  const grouped = new Map<string, CelebrationRecord[]>();
  for (const rec of allRecords) {
    const existing = grouped.get(rec.eventKey) ?? [];
    existing.push(rec);
    grouped.set(rec.eventKey, existing);
  }

  for (const [eventKey, records] of grouped.entries()) {
    const docId = canonicalDocId(userId, eventKey);
    // Check if already migrated (canonical doc exists)
    const canonical = await getDocument<CelebrationRecord>(`${CELEBRATIONS_COLLECTION}/${docId}`);

    // Sort by earnedAt ascending
    const sorted = records.sort((a, b) => (a.earnedAt ?? 0) - (b.earnedAt ?? 0));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const count = sorted.length;

    // Write canonical doc
    await setDocumentWithId(CELEBRATIONS_COLLECTION, docId, {
      userId,
      tier: first.tier,
      eventKey,
      title: last.title || last.message,
      message: last.message,
      subtext: last.subtext,
      count,
      firstAwardedAt: first.earnedAt,
      lastAwardedAt: last.earnedAt,
      lastAwardedMonth: canonical?.lastAwardedMonth ?? getCurrentMonth(),
      earnedAt: last.earnedAt,
      source: last.source ?? 'nutrition',
      version: RECORD_VERSION,
      deviceDate: last.deviceDate ?? getLocalDateString(),
    });

    // Delete all old docs that are not the canonical doc
    for (const rec of records) {
      if (rec.id && rec.id !== docId) {
        await deleteDocument(`${CELEBRATIONS_COLLECTION}/${rec.id}`);
      }
    }
  }

  console.log('[celebration] Migration complete:', grouped.size, 'event keys processed');
}

// ─── WHtR category ────────────────────────────────────────────────────────────

type WHtRCategory = 'low' | 'moderate' | 'high';

function getWhtrCategory(whtr: number): WHtRCategory {
  if (whtr < WHTR_LOW) return 'low';
  if (whtr < WHTR_HIGH) return 'moderate';
  return 'high';
}

const WHTR_CATEGORY_RANK: Record<WHtRCategory, number> = {
  low: 0, moderate: 1, high: 2,
};

// ─── Retroactive edit guard ───────────────────────────────────────────────────
// Weight-based triggers only fire when a body stats entry was saved today.
// This prevents historical edits from triggering celebrations.

async function hasTodayBodyStatsEntry(userId: string): Promise<boolean> {
  const today = getTodayDate();
  const entries = await getBodyStatsEntriesForDateRange(userId, today, today).catch(() => []);
  return entries.length > 0;
}

// ─── Gold triggers ────────────────────────────────────────────────────────────

async function evaluateGold(
  userId: string,
  todayHasWeightEntry: boolean,
): Promise<CelebrationResult | null> {
  const [bodyMetrics, profile] = await Promise.all([
    getBodyMetrics(userId).catch(() => null),
    getUserProfile(userId).catch(() => null),
  ]);

  if (profile?.notifications_enabled === false) return null;

  // 1. Target Weight Reached
  if (
    todayHasWeightEntry &&
    bodyMetrics?.currentWeight &&
    bodyMetrics?.goalWeight &&
    bodyMetrics.currentWeight <= bodyMetrics.goalWeight
  ) {
    const key = 'target_weight_reached';
    if (!await hasEventBeenAwarded(userId, key)) {
      return { tier: 'gold', eventKey: key, message: 'Target weight reached', subtext: 'Milestone achieved — stored permanently' };
    }
  }

  // 2. WHtR Category Improvement (2 consecutive waist entries)
  if (todayHasWeightEntry && bodyMetrics?.height) {
    const entries = await getBodyStatsEntriesForDateRange(userId, getDateDaysAgo(90), getTodayDate()).catch(() => []);
    const waistEntries = entries.filter(e => e.waistCm != null).sort((a, b) => b.timestamp - a.timestamp);
    if (waistEntries.length >= 2) {
      const current  = getWhtrCategory(waistEntries[0].waistCm! / bodyMetrics.height);
      const previous = getWhtrCategory(waistEntries[1].waistCm! / bodyMetrics.height);
      if (WHTR_CATEGORY_RANK[current] < WHTR_CATEGORY_RANK[previous]) {
        const key = `whtr_improved_to_${current}`;
        if (!await hasEventBeenAwarded(userId, key)) {
          return { tier: 'gold', eventKey: key, message: 'Health risk category improved', subtext: 'Milestone achieved — stored permanently' };
        }
      }
    }
  }

  // 3. First 10 kg Lost
  if (todayHasWeightEntry && bodyMetrics?.currentWeight && profile?.baselineWeight) {
    const loss = Math.round((profile.baselineWeight - bodyMetrics.currentWeight) * 10) / 10;
    if (loss >= 10.0) {
      const key = 'first_10kg_lost';
      if (!await hasEventBeenAwarded(userId, key)) {
        return { tier: 'gold', eventKey: key, message: '10 kg milestone reached', subtext: 'Milestone achieved — stored permanently' };
      }
    }
  }

  // 4. First Week Complete
  if (profile?.createdAt && (Date.now() - profile.createdAt) / 86400000 >= 7) {
    const dates = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i));
    const results = await Promise.allSettled(dates.map(d => getDailyNutrition(userId, d).catch(() => null)));
    const loggedDays = results.filter(r => r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0).length;
    if (loggedDays >= 4) {
      const key = 'first_week_complete';
      if (!await hasEventBeenAwarded(userId, key)) {
        return { tier: 'gold', eventKey: key, message: 'First week complete — foundation established', subtext: 'Milestone achieved — stored permanently' };
      }
    }
  }

  // 5. 30-Day Consistency
  {
    const dates = Array.from({ length: 30 }, (_, i) => getDateDaysAgo(i));
    const results = await Promise.allSettled(dates.map(d => getDailyNutrition(userId, d).catch(() => null)));
    const logged = results.filter(r => r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0).length;
    if (logged >= 25) {
      const key = 'consistency_30_days';
      if (!await hasEventBeenAwarded(userId, key)) {
        return { tier: 'gold', eventKey: key, message: '30 days consistent — habit established', subtext: 'Milestone achieved — stored permanently' };
      }
    }
  }

  // 6. 90-Day Consistency
  {
    const dates = Array.from({ length: 90 }, (_, i) => getDateDaysAgo(i));
    const results = await Promise.allSettled(dates.map(d => getDailyNutrition(userId, d).catch(() => null)));
    const logged = results.filter(r => r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0).length;
    if (logged >= 75) {
      const key = 'consistency_90_days';
      if (!await hasEventBeenAwarded(userId, key)) {
        return { tier: 'gold', eventKey: key, message: '90 days consistent — strong routine established', subtext: 'Milestone achieved — stored permanently' };
      }
    }
  }

  // 7. Perfect Week
  // All four conditions met in the same Mon–Sun calendar week:
  //   ≥5 logged days, avg protein ≥ min target, ≥2 exercise sessions,
  //   avg deficit 200–1200 kcal. One-time Gold — never repeats.
  {
    const key = 'perfect_week';
    if (!await hasEventBeenAwarded(userId, key)) {
      // Current Mon–Sun week dates
      const now = new Date();
      const dayOfWeek = (now.getDay() + 6) % 7; // Mon=0 ... Sun=6
      const weekDates = Array.from({ length: dayOfWeek + 1 }, (_, i) => getDateDaysAgo(dayOfWeek - i));
      const weekStart = weekDates[0];
      const weekEnd = weekDates[weekDates.length - 1];

      const [nutritionResults, workouts, targets] = await Promise.all([
        Promise.allSettled(weekDates.map(d => getDailyNutrition(userId, d).catch(() => null))),
        getWorkoutEntriesForDateRange(userId, weekStart, weekEnd).catch(() => []),
        getTargetRanges(userId).catch(() => null),
      ]);

      const loggedDays = nutritionResults
        .filter(r => r.status === 'fulfilled' && r.value && (r.value as any)?.entries?.length > 0)
        .map(r => (r as PromiseFulfilledResult<any>).value);

      if (loggedDays.length >= 5) {
        const avgProtein = loggedDays.reduce((s: number, n: any) => s + (n?.totalProtein ?? 0), 0) / loggedDays.length;
        const proteinMin = targets?.protein?.min ?? 0;
        const proteinOk = proteinMin === 0 || avgProtein >= proteinMin;

        const exerciseSessions = workouts.filter(w => (w.duration ?? 0) > 0).length;
        const exerciseOk = exerciseSessions >= 2;

          // Fetch real deficit (including activity burn) for each logged day
        const deficitResults = await Promise.allSettled(
          weekDates.map(d => getDailyTDEECached(userId, d).catch(() => null))
        );
        const deficits = deficitResults
          .filter(r => r.status === 'fulfilled' && r.value && (r.value as any)?.deficit != null)
          .map(r => (r as PromiseFulfilledResult<any>).value.deficit as number);
        const avgDeficit = deficits.length > 0 ? deficits.reduce((s, d) => s + d, 0) / deficits.length : 0;
        const deficitOk = deficits.length > 0 && avgDeficit >= 200 && avgDeficit <= 1200;

        if (proteinOk && exerciseOk && deficitOk) {
          return { tier: 'gold', eventKey: key, message: 'Perfect week — logging, nutrition, exercise and deficit all on point', subtext: 'Milestone achieved — stored permanently' };
        }
      }
    }
  }

  return null;
}

// ─── Silver triggers ──────────────────────────────────────────────────────────

async function evaluateSilver(
  userId: string,
  todayHasWeightEntry: boolean,
): Promise<CelebrationResult | null> {
  const bodyMetrics = await getBodyMetrics(userId).catch(() => null);

  // 1. New Lowest Weight
  if (todayHasWeightEntry && bodyMetrics?.currentWeight) {
    const entries = await getBodyStatsEntriesForDateRange(userId, getDateDaysAgo(365), getDateDaysAgo(1)).catch(() => []);
    const prevWeights = entries.filter(e => e.weight != null).map(e => e.weight!);
    if (prevWeights.length > 0 && bodyMetrics.currentWeight < Math.min(...prevWeights)) {
      return { tier: 'silver', eventKey: 'new_lowest_weight', message: 'New lowest weight recorded' };
    }
  }

  // 2. Plateau Broken
  if (todayHasWeightEntry && bodyMetrics?.currentWeight) {
    const entries = await getBodyStatsEntriesForDateRange(userId, getDateDaysAgo(16), getDateDaysAgo(1)).catch(() => []);
    const weightEntries = entries.filter(e => e.weight != null).sort((a, b) => a.timestamp - b.timestamp);
    if (weightEntries.length >= 4) {
      const weights = weightEntries.map(e => e.weight!);
      const tolerance = weights[0] * 0.003;
      const isFlat = weights.every(w => Math.abs(w - weights[0]) <= tolerance);
      if (isFlat && bodyMetrics.currentWeight < Math.min(...weights)) {
        return { tier: 'silver', eventKey: 'plateau_broken', message: 'Plateau broken — progress moving again' };
      }
    }
  }

  // 3. Best Week
  {
    const thisWeekDates = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i));
    const thisWeekData = await Promise.all(thisWeekDates.map(d => getDailyNutrition(userId, d).catch(() => null)));
    const thisLogged = thisWeekData.filter(n => n && (n.entries?.length ?? 0) > 0);
    if (thisLogged.length >= 4) {
      const thisAvg = thisLogged.map(n => n!.totalCalories).reduce((s, v) => s + v, 0) / thisLogged.length;
      const prevWeekDates = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i + 7));
      const prevWeekData = await Promise.all(prevWeekDates.map(d => getDailyNutrition(userId, d).catch(() => null)));
      const prevLogged = prevWeekData.filter(n => n && (n.entries?.length ?? 0) > 0);
      if (prevLogged.length >= 4) {
        const prevAvg = prevLogged.map(n => n!.totalCalories).reduce((s, v) => s + v, 0) / prevLogged.length;
        if (thisAvg < prevAvg) {
          return { tier: 'silver', eventKey: 'best_week', message: 'Best week so far — excellent progress' };
        }
      }
    }
  }

  return null;
}

// ─── Bronze triggers ──────────────────────────────────────────────────────────

async function evaluateBronze(userId: string): Promise<CelebrationResult | null> {
  const today = getTodayDate();
  const [nutrition, targets] = await Promise.all([
    getDailyNutrition(userId, today).catch(() => null),
    getTargetRanges(userId).catch(() => null),
  ]);

  const hasEntries = (nutrition?.entries?.length ?? 0) > 0;

  if (hasEntries && nutrition && targets) {
    const calOk      = targets.calories   ? nutrition.totalCalories  >= (targets.calories.min ?? 0)   && nutrition.totalCalories  <= (targets.calories.max ?? Infinity) : false;
    const proteinOk  = targets.protein?.min  ? nutrition.totalProtein  >= targets.protein.min  : false;
    const hydrationOk= targets.hydration?.min? nutrition.totalHydration>= targets.hydration.min: false;
    const fibreOk    = targets.fibre?.min    ? nutrition.totalFibre    >= targets.fibre.min    : false;
    const met = [calOk, proteinOk, hydrationOk, fibreOk].filter(Boolean).length;

    if (met === 4) return { tier: 'bronze', eventKey: 'perfect_day',        message: 'Perfect day — all key targets hit' };
    if (met === 3) return { tier: 'bronze', eventKey: 'strong_day',         message: 'Strong day — most targets achieved' };
    if (proteinOk) return { tier: 'bronze', eventKey: 'protein_target_hit', message: 'Protein target achieved' };
    if (fibreOk)   return { tier: 'bronze', eventKey: 'fibre_target_hit',   message: 'Fibre target achieved' };
  }

  // Logging Habit Forming
  {
    const dates = Array.from({ length: 7 }, (_, i) => getDateDaysAgo(i));
    const results = await Promise.allSettled(dates.map(d => getDailyNutrition(userId, d).catch(() => null)));
    const logged = results.filter(r => r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0).length;
    if (logged >= 6) return { tier: 'bronze', eventKey: 'logging_habit_forming', message: 'Logging habit forming — consistency building' };
  }

  // Bounce Back
  if (hasEntries) {
    const d1 = await getDailyNutrition(userId, getDateDaysAgo(1)).catch(() => null);
    const d2 = await getDailyNutrition(userId, getDateDaysAgo(2)).catch(() => null);
    if ((!d1 || (d1.entries?.length ?? 0) === 0) && (!d2 || (d2.entries?.length ?? 0) === 0)) {
      return { tier: 'bronze', eventKey: 'bounce_back', message: 'Back on track — consistency restored' };
    }
  }

  return null;
}

// ─── Workout milestone helpers ───────────────────────────────────────────────

// Fetches all workout entries once and derives both gym session count and run/walk distance.
// Avoids two separate Firestore range queries when both are needed in the same evaluation pass.
async function getLifetimeWorkoutStats(userId: string): Promise<{ gymSessions: number; runWalkKm: number }> {
  const entries = await getWorkoutEntriesForDateRange(userId, '2000-01-01', getTodayDate()).catch(() => []);
  const gymSessions = entries.filter(e => e.type === 'weightlifting' && (e.duration ?? 0) > 0).length;
  const runWalkKm = entries
    .filter(e => (e.type === 'run' || e.type === 'walk') && (e.distance ?? 0) > 0)
    .reduce((sum, e) => sum + (e.distance ?? 0), 0);
  return { gymSessions, runWalkKm };
}

// Scans last 365 days — not truly lifetime, but sufficient for all current thresholds (max 60 days).
// TODO: extend to full history if higher thresholds are added in future.
async function getLoggedDaysLast365(userId: string): Promise<number> {
  const dates = Array.from({ length: 365 }, (_, i) => getDateDaysAgo(i));
  const results = await Promise.allSettled(dates.map(d => getDailyNutrition(userId, d).catch(() => null)));
  return results.filter(r => r.status === 'fulfilled' && r.value && (r.value.entries?.length ?? 0) > 0).length;
}

// Uses real Monday–Sunday calendar weeks, not rolling 7-day windows.
// Week 0 = the current calendar week (Mon–today), week 1 = last full Mon–Sun week, etc.
function getCalendarWeekBounds(weeksAgo: number): { start: string; end: string } {
  const now = new Date();
  // Day of week: Mon=0 ... Sun=6
  const dayOfWeek = (now.getDay() + 6) % 7;
  // Start of current week (Monday)
  const currentWeekStart = new Date(now);
  currentWeekStart.setDate(now.getDate() - dayOfWeek);
  currentWeekStart.setHours(0, 0, 0, 0);
  // Shift back by weeksAgo full weeks
  const weekStart = new Date(currentWeekStart);
  weekStart.setDate(currentWeekStart.getDate() - weeksAgo * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { start: fmt(weekStart), end: fmt(weekEnd) };
}

async function getConsecutiveWeeksWithStrength(userId: string, weeksRequired: number): Promise<boolean> {
  // Fetch all workout entries once across the full required span — avoids N separate range queries.
  const spanStart = getCalendarWeekBounds(weeksRequired - 1).start;
  const spanEnd = getTodayDate();
  const allEntries = await getWorkoutEntriesForDateRange(userId, spanStart, spanEnd).catch(() => []);
  const gymEntries = allEntries.filter(e => e.type === 'weightlifting' && (e.duration ?? 0) > 0);

  for (let week = 0; week < weeksRequired; week++) {
    const { start, end } = getCalendarWeekBounds(week);
    const sessions = gymEntries.filter(e => e.date >= start && e.date <= end).length;
    if (sessions < 2) return false;
  }
  return true;
}

// ─── Gym Training Milestones ──────────────────────────────────────────────────

async function evaluateGymMilestones(userId: string, gymSessions: number): Promise<CelebrationResult | null> {
  if (gymSessions >= 30) {
    const key = 'gym_sessions_gold';
    if (!await hasEventBeenAwarded(userId, key)) {
      return { tier: 'gold', eventKey: key, message: "You're protecting muscle while losing weight", subtext: 'Milestone achieved — stored permanently' };
    }
  }
  if (gymSessions >= 15) {
    const key = 'gym_sessions_silver';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'silver', eventKey: key, message: 'Strength training is becoming routine' };
    }
  }
  if (gymSessions >= 5) {
    const key = 'gym_sessions_bronze';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'bronze', eventKey: key, message: "You've started building strength" };
    }
  }
  return null;
}

// ─── Run / Walk Distance Milestones ──────────────────────────────────────────

async function evaluateRunWalkMilestones(userId: string, runWalkKm: number): Promise<CelebrationResult | null> {
  if (runWalkKm >= 300) {
    const key = 'distance_gold';
    if (!await hasEventBeenAwarded(userId, key)) {
      return { tier: 'gold', eventKey: key, message: 'Movement is now part of your lifestyle', subtext: 'Milestone achieved — stored permanently' };
    }
  }
  if (runWalkKm >= 150) {
    const key = 'distance_silver';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'silver', eventKey: key, message: 'Your fitness is improving' };
    }
  }
  if (runWalkKm >= 50) {
    const key = 'distance_bronze';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'bronze', eventKey: key, message: "You're building endurance" };
    }
  }
  return null;
}

// ─── Logging Days Milestones ─────────────────────────────────────────────────

async function evaluateLoggingDaysMilestones(userId: string): Promise<CelebrationResult | null> {
  const days = await getLoggedDaysLast365(userId);

  if (days >= 60) {
    const key = 'logging_days_gold';
    if (!await hasEventBeenAwarded(userId, key)) {
      return { tier: 'gold', eventKey: key, message: '60 days of logging — this is now part of who you are', subtext: 'Milestone achieved — stored permanently' };
    }
  }
  if (days >= 30) {
    const key = 'logging_days_silver';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'silver', eventKey: key, message: '30 days logged — your tracking habit is forming' };
    }
  }
  if (days >= 10) {
    const key = 'logging_days_bronze';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'bronze', eventKey: key, message: '10 days logged — a solid start' };
    }
  }
  return null;
}

// ─── Strength Protection Milestones ──────────────────────────────────────────

async function evaluateStrengthProtectionMilestones(userId: string): Promise<CelebrationResult | null> {
  if (await getConsecutiveWeeksWithStrength(userId, 8)) {
    const key = 'strength_protection_gold';
    if (!await hasEventBeenAwarded(userId, key)) {
      return { tier: 'gold', eventKey: key, message: "You've built a strong training rhythm", subtext: 'Milestone achieved — stored permanently' };
    }
  }
  if (await getConsecutiveWeeksWithStrength(userId, 4)) {
    const key = 'strength_protection_silver';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'silver', eventKey: key, message: 'Your body is adapting to training' };
    }
  }
  if (await getConsecutiveWeeksWithStrength(userId, 2)) {
    const key = 'strength_protection_bronze';
    if (!await hasEventBeenAwardedThisMonth(userId, key)) {
      return { tier: 'bronze', eventKey: key, message: "You're protecting muscle" };
    }
  }
  return null;
}

// ─── Main evaluation entry point ──────────────────────────────────────────────

export async function evaluateCelebration(
  userId: string,
  sourceScreen = 'home',
): Promise<CelebrationResult | null> {
  if (!userId) return null;
  if (hasShownCelebrationToday()) return null;

  const todayHasWeightEntry = await hasTodayBodyStatsEntry(userId).catch(() => false);

  // NOTE: Evaluation cost is growing as milestone families expand.
  // If performance degrades, consider batching workout/nutrition queries before
  // running evaluators, and caching results within a single evaluation pass.

  // Priority order: Gold → Silver → StrengthProtection → GymSessions → Bronze → RunWalk → LoggingDays
  // Strength protection and gym milestones rank above generic Bronze because for GLP-1 users
  // strength training is the highest-value behaviour to reinforce.
  let result: CelebrationResult | null = null;
  try { result = await evaluateGold(userId, todayHasWeightEntry); }
  catch (err) { console.error('[celebration] Gold evaluation failed:', err); }

  if (!result) {
    try { result = await evaluateSilver(userId, todayHasWeightEntry); }
    catch (err) { console.error('[celebration] Silver evaluation failed:', err); }
  }

  if (!result) {
    try { result = await evaluateStrengthProtectionMilestones(userId); }
    catch (err) { console.error('[celebration] Strength protection milestones evaluation failed:', err); }
  }

  // Fetch lifetime workout stats once — shared by gym and run/walk evaluators
  let workoutStats = { gymSessions: 0, runWalkKm: 0 };
  try { workoutStats = await getLifetimeWorkoutStats(userId); } catch { /* non-fatal */ }

  if (!result) {
    try { result = await evaluateGymMilestones(userId, workoutStats.gymSessions); }
    catch (err) { console.error('[celebration] Gym milestones evaluation failed:', err); }
  }

  if (!result) {
    try { result = await evaluateBronze(userId); }
    catch (err) { console.error('[celebration] Bronze evaluation failed:', err); }
  }

  if (!result) {
    try { result = await evaluateRunWalkMilestones(userId, workoutStats.runWalkKm); }
    catch (err) { console.error('[celebration] Run/walk milestones evaluation failed:', err); }
  }

  if (!result) {
    try { result = await evaluateLoggingDaysMilestones(userId); }
    catch (err) { console.error('[celebration] Logging days milestones evaluation failed:', err); }
  }

  if (!result) return null;

  const deviceDate = getLocalDateString();
  const source: CelebrationRecord['source'] =
    ['weight', 'whtr', 'kg', 'plateau', 'lowest'].some(k => result!.eventKey.includes(k))
      ? 'weight'
      : ['gym', 'distance', 'strength', 'logging_days'].some(k => result!.eventKey.includes(k))
      ? 'workout'
      : 'nutrition';

  const baseRecord = {
    userId, tier: result.tier, eventKey: result.eventKey,
    title: result.message, message: result.message, subtext: result.subtext,
    earnedAt: Date.now(), shownAt: Date.now(), sourceScreen,
    version: RECORD_VERSION, deviceDate, source,
    count: 1, firstAwardedAt: Date.now(), lastAwardedAt: Date.now(),
  };
  try {
    if (result.tier === 'gold') {
      await storeGoldCelebration(baseRecord);
    } else {
      await upsertRepeatableCelebration(baseRecord);
    }
  } catch (err) {
    console.error('[celebration] Failed to store record:', err);
  }

  markCelebrationShownToday();
  return result;
}
